import { useEffect, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArchiproMark } from '@rocco/icons/svg/logo-new-black.svg';
import {
    DetailBadgeRow,
    DetailSaveButton,
    DetailSummaryRow,
    TrackedWebsiteCta,
    detailCta,
} from '~/ui-rocco/pages/detail-shared';
import { DETAIL_LOGO_BADGE } from '~/ui-rocco/pages/detail-shared/DetailLogoBadge';
import { ShareSheet } from '~/modules/moodboards/components/ShareSheet';
import { TileActionPanel } from '~/modules/moodboards/components/TileActionPanel';
import {
    useBookCta,
    useEnquireCta,
} from '~/modules/enquiry/hooks/use-page-ctas';
import { ARTICLE_GROUND } from '../util/article-ground';
import type { RevampProfessional } from '../api/get-article-revamp-data.server';

/**
 * The opening paragraph's type: the product statement's size and measure, in
 * Gestura as the directory's own statement is set, and always in full — an
 * article's intro is written to be read, not cropped to three lines.
 */
const OPENING_TYPE = cn(
    'm-0 font-gestura font-normal text-[#323232]',
    'max-md:text-[1.375rem] max-md:leading-[1.2] max-md:tracking-[-0.02em]',
    'md:text-[2.0625rem] md:leading-[1.25] md:tracking-[-0.02em]',
);

/**
 * The Website referral source the legacy article page stamped from its
 * company profile footer, on desktop and phone alike, kept so the article's
 * referral series runs on unbroken.
 */
const WEBSITE_SOURCE = 'CompanyProfile';

/** The product page's Save button surface: white, lifted, no hairline. */
const SAVE_LIFTED =
    'border-transparent bg-white text-[#181818] [box-shadow:0px_4px_14px_10px_rgba(0,0,0,0.05)] hover:bg-white/70';

interface RevampOpeningRowProps {
    /** The article's opening paragraph, leading the row as the product's description does. */
    teaser?: string;
    articleID: number;
    articleTitle: string;
    /** The article's own path, for Share. */
    shareLink: string;
    /** The opening picture, for the share pin and the page sheet. */
    heroImage?: string;
    /**
     * The one client the article is about, when there is exactly one and it
     * is not ArchiPro's own editorial account. Absent, the row offers the
     * article itself: share and save. Present, the row must sit inside the
     * article's page CTAs provider.
     */
    client?: RevampProfessional;
}

/**
 * The client's enquiry button, then Book a meeting beside it when the
 * professional has assigned one to article pages. Both rows of the summary
 * mount this.
 */
const OpeningEnquiryButtons = ({
    clientTitle,
    mobile = false,
}: {
    clientTitle: string;
    mobile?: boolean;
}) => {
    const enquire = useEnquireCta({ source: 'ArticleOpeningRow' });
    const book = useBookCta({ source: 'ArticleOpeningRow' });
    const className = mobile
        ? detailCta('white-lifted', 'w-full')
        : detailCta('white-lifted');

    return (
        <>
            {enquire.visible ? (
                <button
                    type="button"
                    onClick={() =>
                        enquire.open({ title: `Contact ${clientTitle}` })
                    }
                    className={className}
                >
                    {enquire.label}
                </button>
            ) : null}
            {book.inline ? (
                <button type="button" onClick={book.open} className={className}>
                    {book.label}
                </button>
            ) : null}
        </>
    );
};

/**
 * The product page's summary row, under the article's opening picture: the
 * opening paragraph on the left at the statement size, in full, and the
 * actions ranged right. With a client, the actions are theirs — Enquire
 * Now (and Book a meeting when assigned), Visit Website and Save. Without one, they are the article's — Share
 * Article and Save Article.
 */
export const RevampOpeningRow = ({
    teaser,
    articleID,
    articleTitle,
    shareLink,
    heroImage,
    client,
}: RevampOpeningRowProps) => {
    // The share targets want an absolute address, which only the browser
    // knows; the path stands in until it has mounted.
    const [origin, setOrigin] = useState('');
    useEffect(() => setOrigin(window.location.origin), []);

    if (!teaser && !client) return null;

    const share = {
        url: origin ? `${origin}${shareLink}` : shareLink,
        title: articleTitle,
        image: heroImage && origin ? `${origin}${heroImage}` : heroImage,
    };

    const desktopActions = client ? (
        <>
            <OpeningEnquiryButtons clientTitle={client.title} />
            {client.website ? (
                <TrackedWebsiteCta
                    website={client.website}
                    libraryItemID={articleID}
                    professionalID={client.id}
                    professionalTitle={client.title}
                    variant="white-lifted"
                    source={WEBSITE_SOURCE}
                />
            ) : null}
            {/* Grows its label out on hover and keyboard focus, as the
                product, project and profile clusters do: beside two named
                pills, a bare bookmark is the one control in the row the
                reader has to guess at. */}
            <DetailSaveButton
                pinId={articleID}
                pinType="ArticlePin"
                hoverLabel
                className={SAVE_LIFTED}
            />
        </>
    ) : (
        <>
            <ShareSheet share={share}>
                {({ open, toggle }) => (
                    <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={open}
                        onClick={toggle}
                        className={detailCta('white-lifted')}
                    >
                        Share Article
                    </button>
                )}
            </ShareSheet>
            <DetailSaveButton
                pinId={articleID}
                pinType="ArticlePin"
                label="Save Article"
                className={SAVE_LIFTED}
            />
        </>
    );

    // The product page's phone cluster: a full-width white lifted primary,
    // then Visit Website beside the `•••` pill that opens the page sheet —
    // Share, Save, Save to Pinterest — on the same grey ground the product
    // page lifts them from.
    const mobileActions = client ? (
        <>
            <OpeningEnquiryButtons clientTitle={client.title} mobile />
            <div className="flex items-stretch gap-2">
                {client.website ? (
                    <TrackedWebsiteCta
                        website={client.website}
                        libraryItemID={articleID}
                        professionalID={client.id}
                        professionalTitle={client.title}
                        variant="white-lifted"
                        source={WEBSITE_SOURCE}
                        className="flex-1"
                    />
                ) : null}
                <TileActionPanel
                    pinId={articleID}
                    pinType="ArticlePin"
                    bareTrigger
                    sheetLayout="page"
                    triggerClassName={cn(
                        detailCta('white-lifted'),
                        'm-0 h-[3.25rem] w-[3.25rem] shrink-0 p-0',
                    )}
                    share={share}
                    tileInfo={{
                        title: articleTitle,
                        subtitle: client.title,
                        image: heroImage,
                        imageShape: 'square',
                    }}
                />
            </div>
        </>
    ) : (
        <>
            <ShareSheet share={share}>
                {({ open, toggle }) => (
                    <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={open}
                        onClick={toggle}
                        className={detailCta('white-lifted', 'w-full')}
                    >
                        Share Article
                    </button>
                )}
            </ShareSheet>
            <DetailSaveButton
                pinId={articleID}
                pinType="ArticlePin"
                label="Save Article"
                className={cn(SAVE_LIFTED, 'w-full')}
            />
        </>
    );

    return (
        <DetailSummaryRow
            // The article's one phone gutter is the prose's 18px, over the
            // row's shared 24.
            className={cn(ARTICLE_GROUND, 'max-md:px-4.5')}
            description={
                teaser ? <p className={OPENING_TYPE}>{teaser}</p> : undefined
            }
            descriptionClassName="max-md:mt-0 md:mt-0 md:max-w-[60rem]"
            aside={
                <>
                    <div className="flex w-full flex-col gap-3 md:hidden">
                        {mobileActions}
                    </div>
                    <div className="hidden flex-wrap items-center justify-end gap-2 md:flex">
                        {desktopActions}
                    </div>
                </>
            }
        />
    );
};

/**
 * The badge straddling the opening picture's lower edge on the right, as the
 * supplier's does on the product page: the client's logo, linking to their
 * profile, or ArchiPro's own mark when the article has no single client. The
 * shared row owns the geometry.
 */
export const RevampOpeningBadge = ({
    client,
}: {
    client?: RevampProfessional;
}) => {
    if (client && !client.logo) return null;

    return (
        <DetailBadgeRow
            logo={
                client?.logo
                    ? {
                          src: client.logo,
                          alt: `${client.title} logo`,
                          background: client.logoBackground,
                          link: client.link,
                          ariaLabel: `${client.title} profile`,
                      }
                    : undefined
            }
            fallback={
                <span
                    aria-label="ArchiPro"
                    className={cn(
                        DETAIL_LOGO_BADGE,
                        'pointer-events-auto flex items-center justify-center bg-white',
                    )}
                >
                    <ArchiproMark aria-hidden className="h-[4.25rem] w-auto" />
                </span>
            }
        />
    );
};
